import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import * as FileSaver from 'file-saver';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { ActualSelected, Utils } from '../../../../../shared/utils';
import { mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';
import { investmentListPayload, investmentSearchFromState } from '../../../shared/investment-list.util';
import { consts } from '../../../../../consts';

import { InvestmentService } from '../../../../../modules/investment/investment.service';
import { InvestmentDialogPayComponent } from '../../../../../modules/investment/intangible-material/investment-dialog-pay.component';
import { AmortisationTableDialogComponent } from '../../../../../modules/investment/amortisation/table-dialog.component';
import { AccountingManualFormComponent } from '../../../../../modules/accounting/manual/accounting-manual-form.component';
import { AddFileService } from '../../../../../shared/components/add-file/add-file.service';

import {
  amortisationLabel,
  amortisationVariant,
  INVESTMENT_ASSET_KINDS,
  InvestmentAssetKind,
  InvestmentAssetListSummary,
  InvestmentAssetRow,
  InvestmentAssetType,
  paymentLabel,
  paymentVariant,
  roundMoney,
  isNonDepreciable,
} from '../../models/investment-asset.model';
import { InvestmentAssetReadService } from '../../services/investment-asset-read.service';
import { InvestmentAssetEntityService } from '../../services/investment-asset-entity.service';
import { InvestmentAssetFormDialogComponent } from '../investment-asset-form-dialog/investment-asset-form-dialog.component';
import { createInvestmentAssetTableConfig, InvestmentAssetRowAction } from './investment-asset-table.config';

@Component({
  standalone: false,
  selector: 'app-investment-asset-list',
  templateUrl: './investment-asset-list.component.html',
  styleUrls: ['./investment-asset-list.component.css']
})
export class InvestmentAssetListComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() type: InvestmentAssetType = 1;

  @ViewChild('table', { static: false }) t1!: CustomTableComponent;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;
  @ViewChild('assetSummary') assetSummaryTpl!: TemplateRef<any>;
  @ViewChild('expandDetail') expandDetailTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;
  kind!: InvestmentAssetKind;

  readonly year = ActualSelected.yearSelected;

  quickSearch = '';
  totals = { count: 0, totalWithoutTax: 0, total: 0, paid: 0, pending: 0 };
  selectedUids: string[] = [];

  private rows: InvestmentAssetRow[] = [];
  private selectedRows: Record<string, InvestmentAssetRow> = {};
  private summary: InvestmentAssetListSummary | null = null;
  private totalItems = 0;
  private lastState: TableStateSnapshot | null = null;
  private loadSeq = 0;

  private readonly quickSearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private reads: InvestmentAssetReadService,
    private entity: InvestmentAssetEntityService,
    private investmentService: InvestmentService,
    private addFileService: AddFileService,
    private loading: LoadingDialogService,
    private notify: NotificationService,
    private confirm: ConfirmService,
    private tier: TierService,
    private cdr: ChangeDetectorRef,
  ) { }

  get hasSelection(): boolean {
    return this.selectedUids.length > 0;
  }

  ngOnInit(): void {
    this.kind = INVESTMENT_ASSET_KINDS[this.type] || INVESTMENT_ASSET_KINDS[1];
    this.resetStoredColumns();
    const money = (val: number) => Utils.FormatMoney(val ?? 0) + ' €';
    const date = (val: any) => this.formatApiDate(val);

    this.tableConfig = createInvestmentAssetTableConfig(
      {
        actionMap: {
          [InvestmentAssetRowAction.Edit]: (row) => this.edit(row),
          [InvestmentAssetRowAction.Delete]: (row) => this.delete(row),
          [InvestmentAssetRowAction.DownloadImage]: (row) => this.downloadImage(row),
          [InvestmentAssetRowAction.DownloadPdf]: (row) => this.downloadPdf(row),
          [InvestmentAssetRowAction.Amortisation]: (row) => this.showAmortisationTable(row),
          [InvestmentAssetRowAction.Pay]: (row) => this.pay(row),
          [InvestmentAssetRowAction.Contabilizar]: () => this.addManualAccountMovements(),
          [InvestmentAssetRowAction.AddAmortisationAccounting]: (row) => this.addAmortisationAccounting(row),
          [InvestmentAssetRowAction.DeleteAmortisationAccounting]: (row) => this.deleteAmortisationAccounting(row),
        },
        selectable: true,
        columns: [
          { key: 'supplier', label: 'Proveedor', sortable: true, sortKey: 'Supplier', ellipsis: true, maxWidth: '170px', onCellClick: (row: InvestmentAssetRow) => this.edit(row) },
          { key: 'description', label: 'Descripción', sortable: true, sortKey: 'Description', ellipsis: true, maxWidth: '170px' },
          { key: 'account', label: 'Cuenta', sortable: true, sortKey: 'Account', hidden: true },
          { key: 'purchaseDate', label: 'Fecha de compra', sortable: true, sortKey: 'PurchaseDate', formatFn: date },
          { key: 'startWorkingDate', label: 'Puesta en funcionamiento', sortable: true, sortKey: 'StartWorkingDate', formatFn: date, hidden: true },
          { key: 'totalWithoutTax', label: 'Total sin impuestos', sortable: true, sortKey: 'TotalWithoutTax', formatFn: money, hidden: true },
          { key: 'totalTax', label: 'Impuestos', sortable: true, sortKey: 'TotalTax', formatFn: money, hidden: true },
          { key: 'total', label: 'Total', sortable: true, sortKey: 'Total', formatFn: money },
          { key: 'paid', label: 'Pagado', sortable: true, sortKey: 'Paid', formatFn: money, hidden: true },
          { key: 'pending', label: 'Pendiente', sortable: true, sortKey: 'Pending', hidden: true, formatFn: (val: number) => money(Math.max(Number(val) || 0, 0)) },
          {
            key: 'paymentStatus', label: 'Pago', sortable: true, sortKey: 'PaymentStatus',
            badge: {
              variantFn: (_: number, row: InvestmentAssetRow) => paymentVariant(row),
              labelFn: (_: number, row: InvestmentAssetRow) => paymentLabel(row),
            },
          },
          {
            key: 'amortisationYearExists', label: 'Amortización ' + this.year, sortable: false,
            badge: {
              variantFn: (_: boolean, row: InvestmentAssetRow) => amortisationVariant(row),
              labelFn: (_: boolean, row: InvestmentAssetRow) => amortisationLabel(row),
            },
          },
        ],
        headerActions: [
          { label: '', icon: 'add', type: 'button', callback: () => this.create() },
        ],
        gridActions: [
          { label: 'Exportar', icon: 'file_download', type: 'button', callback: () => this.exportExcel() },
          { label: 'Libro de inversión/amortización', icon: 'file_download', type: 'button', callback: () => this.bookInvestmentAmortisation() },
          { label: 'Amortizar seleccionadas', icon: 'show_chart', type: 'button', callback: () => this.amortisationSelected() },
          { label: 'Contabilizar (asiento libre)', icon: 'show_chart', type: 'button', callback: () => this.addManualAccountMovements() },
        ],
        rowConfig: {
          onToggleSelect: (row: InvestmentAssetRow, selected: boolean) => this.toggleSelection(row, selected),
        },
      },
      { filterView: this.kind.filterView }
    );

    this.tableConfig.rowConfig = {
      ...this.tableConfig.rowConfig,
      expandable: true,
      onExpand: () => { },
      onCollapse: () => { },
      onToggleExpand: () => { },
    };
  }

  ngAfterViewInit(): void {
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    this.tableConfig.summaryTemplate = this.assetSummaryTpl;
    this.tableConfig.expandableRowTemplate = this.expandDetailTpl;

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        const search = investmentSearchFromState(state);
        if (search !== this.quickSearch) this.quickSearch = search;
        this.lastState = state;
        this.load();
      });

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  reload(): void {
    this.load();
  }

  create(): void {
    InvestmentAssetFormDialogComponent.open(this.dialog, { type: this.type })
      .subscribe(saved => { if (saved) this.load(); });
  }

  edit(row: InvestmentAssetRow): void {
    if (!row || !row.uid) return;
    InvestmentAssetFormDialogComponent.open(this.dialog, { type: this.type, uid: row.uid })
      .subscribe(saved => { if (saved) this.load(); });
  }

  delete(row: InvestmentAssetRow): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.confirm.open({
      title: 'Eliminar inversión',
      subtitle: [row.description, row.supplier].filter(Boolean).join(' · ') || undefined,
      message: 'Se eliminarán la inversión, sus pagos y sus asientos contables, y se desvinculará de las subvenciones asociadas. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.rowDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando inversión…',
      errorText: 'No se ha podido eliminar la inversión',
      action: () => this.entity.delete(row.uid),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Inversión eliminada correctamente');
      this.unselect(row.uid);
      this.load();
    });
  }

  pay(row: InvestmentAssetRow): void {
    if (!this.tier.requireDocumentsTier()) return;
    InvestmentDialogPayComponent.open(this.dialog, { investment: this.payTarget(row) })
      .subscribe(changed => { if (changed) this.load(); });
  }

  showAmortisationTable(row: InvestmentAssetRow): void {
    if (!this.tier.requireDocumentsTier()) return;
    AmortisationTableDialogComponent.open(this.dialog, { uid: row.uid, name: row.description, year: Number(this.year) || undefined }).subscribe();
  }

  addAmortisationAccounting(row: InvestmentAssetRow): void {
    if (!this.tier.requireDocumentsTier()) return;
    let message = '';
    this.confirm.open({
      title: 'Contabilizar amortización',
      subtitle: [row.description, row.supplier].filter(Boolean).join(' · ') || undefined,
      message: `Se generará el asiento de amortización del ejercicio ${this.year} de esta inversión.`,
      tone: 'info',
      icon: 'show_chart',
      details: this.rowDetails(row),
      confirmText: 'Contabilizar',
      confirmIcon: 'check_circle',
      loadingText: 'Contabilizando amortización…',
      errorText: 'No se pudo contabilizar la amortización',
      action: () => this.entity.addAmortisationAccounting(row.uid).then(result => { message = result; }),
    }).subscribe(done => {
      if (!done) return;
      this.notify.ok(message || 'Amortización contabilizada correctamente');
      this.load();
    });
  }

  deleteAmortisationAccounting(row: InvestmentAssetRow): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.confirm.open({
      title: 'Eliminar contabilización de la amortización',
      subtitle: [row.description, row.supplier].filter(Boolean).join(' · ') || undefined,
      message: `Se eliminará el asiento de amortización del ejercicio ${this.year} de esta inversión.`,
      tone: 'danger',
      details: this.rowDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando asiento de amortización…',
      errorText: 'No se ha eliminado la contabilidad de la amortización',
      action: () => this.entity.deleteAmortisationAccounting(row.uid),
    }).subscribe(done => {
      if (!done) return;
      this.notify.ok('Se ha eliminado correctamente la contabilidad de la amortización');
      this.load();
    });
  }

  addManualAccountMovements(): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.dialog.open(AccountingManualFormComponent, {
      width: '90vw',
      maxWidth: '96vw',
      height: '70%',
      data: { accountingMovementId: '' }
    }).afterClosed().subscribe((result: string) => {
      if (!Utils.IsNullOrEmpty(result) && result === 'OK') this.load();
    });
  }

  downloadImage(row: InvestmentAssetRow): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.loading.run('Descargando documento…', this.addFileService.downloadFileImage(row.uid, consts.INVESTMENT_ENTITY, consts.EMPTY_GUID))
      .then(blob => FileSaver.saveAs(blob, Utils.GetFileName('imagen_fichero', consts.EXT_PNG)))
      .catch(() => this.notify.error('No se pudo descargar la imagen'));
  }

  downloadPdf(row: InvestmentAssetRow): void {
    if (!this.tier.requireDocumentsTier()) return;
    this.loading.run('Descargando documento…', this.addFileService.downloadFilePDF(row.uid, consts.INVESTMENT_ENTITY, consts.EMPTY_GUID))
      .then(blob => FileSaver.saveAs(blob, Utils.GetPdfName('Inversión')))
      .catch(() => this.notify.error('No se pudo descargar el PDF'));
  }

  exportExcel(): void {
    this.loading.run('Exportando inversiones…', this.investmentService.exportInvestments())
      .then(blob => FileSaver.saveAs(blob, Utils.GetExcelName('Inversiones')))
      .catch(() => this.notify.error('No se pudo exportar el Excel'));
  }

  bookInvestmentAmortisation(): void {
    if (!this.tier.requireDocumentsTier()) return;
    const book = this.bookUids().then(uids => {
      if (!uids.length) throw { empty: true };
      return this.investmentService.getBookAmortisation(uids, consts.EMPTY_GUID);
    });
    this.loading.run('Generando libro de inversión/amortización…', book)
      .then(blob => FileSaver.saveAs(blob, Utils.GetExcelName('Libro_Inversion_Amortización')))
      .catch(error => {
        if (error && error.empty) this.notify.warning('No hay inversiones para generar el libro');
        else this.notify.error('No se pudo generar el libro');
      });
  }

  amortisationSelected(): void {
    if (!this.tier.requireDocumentsTier()) return;
    const rows = this.selectedUids.map(uid => this.selectedRows[uid]).filter(row => !!row);
    if (!rows.length) {
      this.notify.warning('No hay filas seleccionadas');
      return;
    }
    const pending = rows.filter(row => !row.amortisationYearExists && !isNonDepreciable(row));
    if (!pending.length) {
      this.notify.warning(rows.some(row => isNonDepreciable(row))
        ? `Las inversiones seleccionadas no se pueden amortizar (terrenos) o ya tienen contabilizada la amortización de ${this.year}`
        : `Las inversiones seleccionadas ya tienen contabilizada la amortización de ${this.year}`);
      return;
    }

    const failures: string[] = [];
    this.confirm.open({
      title: 'Amortizar seleccionadas',
      message: `Se generará el asiento de amortización del ejercicio ${this.year} de ${pending.length} ${pending.length === 1 ? 'inversión' : 'inversiones'}. Las que ya estén contabilizadas no se tocan.`,
      tone: 'info',
      icon: 'show_chart',
      details: [
        { label: 'Seleccionadas', value: String(rows.length) },
        { label: 'Pendientes de amortizar', value: String(pending.length) },
      ],
      confirmText: 'Contabilizar',
      confirmIcon: 'check_circle',
      loadingText: 'Contabilizando amortizaciones…',
      errorText: 'No se pudieron contabilizar las amortizaciones',
      action: () => pending.reduce(
        (chain, row) => chain.then(() => this.entity.addAmortisationAccounting(row.uid)
          .then(() => undefined)
          .catch(error => { failures.push((row.description || row.supplier || 'Inversión') + ': ' + ((error && error.error && error.error.message) || 'error')); })),
        Promise.resolve()
      ),
    }).subscribe(done => {
      if (!done) return;
      if (failures.length) {
        this.notify.warning(`${pending.length - failures.length} contabilizadas, ${failures.length} con error. ${failures[0]}`);
      } else {
        this.notify.ok('Amortizaciones contabilizadas correctamente');
      }
      this.load();
    });
  }

  private load(): void {
    if (!this.t1 || !this.lastState) return;
    const seq = ++this.loadSeq;
    this.t1.tableState.setLoading(true);
    this.reads.list(this.kind, investmentListPayload(this.lastState))
      .then(result => {
        if (seq !== this.loadSeq) return;
        const expanded = new Set(this.rows.filter(item => item.__expanded).map(item => item.uid));
        this.rows = result.items.map(item => {
          const selected = this.selectedUids.indexOf(item.uid) !== -1;
          const row = { ...item, __expanded: expanded.has(item.uid), __selected: selected };
          if (selected) this.selectedRows[item.uid] = row;
          return row;
        });
        this.summary = result.summary;
        this.totalItems = result.total;
        this.recomputeTotals();
        this.t1.tableState.setData(this.rows);
        this.t1.tableState.setTotalItems(result.total);
        this.t1.tableState.setLoading(false);
        this.cdr.markForCheck();
      })
      .catch(error => {
        if (seq !== this.loadSeq) return;
        this.t1.tableState.setLoading(false);
        this.notify.error((error && error.error && error.error.message) || 'No se pudo cargar la lista de inversiones');
      });
  }

  private bookUids(): Promise<string[]> {
    if (this.hasSelection) return Promise.resolve(this.selectedUids.slice());
    if (!this.lastState) return Promise.resolve([]);
    const payload = { ...investmentListPayload(this.lastState), page: 1, pageSize: 99999 };
    return this.reads.list(this.kind, payload).then(result => result.items.map(item => item.uid));
  }

  private toggleSelection(row: InvestmentAssetRow, selected: boolean): void {
    if (!row || !row.uid) return;
    if (selected) {
      if (this.selectedUids.indexOf(row.uid) === -1) this.selectedUids.push(row.uid);
      this.selectedRows[row.uid] = row;
    } else {
      this.unselect(row.uid);
    }
    this.recomputeTotals();
    this.cdr.markForCheck();
  }

  private unselect(uid: string): void {
    this.selectedUids = this.selectedUids.filter(item => item !== uid);
    delete this.selectedRows[uid];
  }

  private recomputeTotals(): void {
    if (!this.hasSelection) {
      const summary = this.summary;
      this.totals = {
        count: this.totalItems,
        totalWithoutTax: roundMoney(summary ? summary.totalWithoutTax : 0),
        total: roundMoney(summary ? summary.total : 0),
        paid: roundMoney(summary ? summary.paid : 0),
        pending: roundMoney(summary ? summary.pending : 0),
      };
      return;
    }
    const source = this.selectedUids.map(uid => this.selectedRows[uid]).filter(row => !!row);
    this.totals = {
      count: source.length,
      totalWithoutTax: roundMoney(source.reduce((acc, row) => acc + (Number(row.totalWithoutTax) || 0), 0)),
      total: roundMoney(source.reduce((acc, row) => acc + (Number(row.total) || 0), 0)),
      paid: roundMoney(source.reduce((acc, row) => acc + (Number(row.paid) || 0), 0)),
      pending: roundMoney(source.reduce((acc, row) => acc + Math.max(Number(row.pending) || 0, 0), 0)),
    };
  }

  private resetStoredColumns(): void {
    const view = this.kind.filterView;
    const marker = view + ':columns-v2';
    try {
      if (localStorage.getItem(marker)) return;
      localStorage.removeItem(view + ':hiddenColumns');
      localStorage.removeItem(view + ':columnOrder');
      localStorage.setItem(marker, '1');
    } catch { }
  }

  private payTarget(row: InvestmentAssetRow) {
    return { uid: row.uid, total: row.total, description: row.description, supplier: row.supplier };
  }

  private rowDetails(row: InvestmentAssetRow): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (row.account) details.push({ label: 'Cuenta', value: row.account });
    const purchase = this.formatApiDate(row.purchaseDate);
    if (purchase) details.push({ label: 'Fecha de compra', value: purchase });
    details.push({ label: 'Total', value: Utils.FormatMoney(row.total ?? 0) + ' €' });
    details.push({ label: 'Pagado', value: Utils.FormatMoney(row.paid ?? 0) + ' €' });
    return details;
  }

  private formatApiDate(value: any): string {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime()) || date.getFullYear() <= 1) return '';
    return formatDate(date, 'dd/MM/yyyy', 'es-ES');
  }
}
